import { useIsDebugEnabled } from "@asantech/common/react/common/hooks/useIsDebugEnabled";
import { ReactComponent as PoweredBy } from "assets/svgs/powered-by-6s.svg";
import { useInfoDisplayMessageWebSocket } from "common/hooks/useInfoDisplaySocket";
import { useIsUpdateAvailable } from "common/hooks/useIsUpdateAvailable";
import { uuidRegExp } from "common/utils/regex";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { InfoDisplayCapacityView } from "views/infoDisplay/CapacityView";
import { InfoDisplayEntranceExitView } from "views/infoDisplay/EntranceExitView";
import { OrientationContainer } from "views/infoDisplay/OrientationContainer";
import {
  CapacityPayload,
  HallsCapacity,
  SettingsPayload,
} from "views/infoDisplay/types";
import { InfoDisplayMessageType } from "../../../backend/src/integration/infoDisplay/types";
import packageJson from "../../package.json";

export const InfoDisplayView = () => {
  const { search } = useLocation();
  const hasUpdate = useIsUpdateAvailable();
  const [settings, setSettings] = useState<SettingsPayload | undefined>(
    undefined
  );
  const handleSettingsMessage = useCallback((payload: SettingsPayload) => {
    setSettings(payload);
  }, []);
  const connectedCapacityHalls = useMemo(() => {
    const iterator = search.matchAll(/(\?|&)capacity=(.*?)(&|$)/g);
    const [, , matches = ""] = Array.from(iterator)[0] || [];
    const entries = matches.split(",").reduce((acc, v, idx, arr) => {
      const prev = arr[idx - 1];

      if (!v) return acc;
      if (v.match(uuidRegExp)) {
        acc[v] = {
          label: "",
          capacity: null,
        };
      } else {
        if (!prev || !prev.match(uuidRegExp)) {
          console.error(
            "Connected capacity halls configuration value is not valid:",
            `${prev},${v}`
          );
        }
        acc[prev].label = decodeURIComponent(v);
      }
      return acc;
    }, {} as HallsCapacity);

    return entries;
  }, [search]);
  const [capacity, setCapacity] = useState<HallsCapacity>(
    connectedCapacityHalls
  );
  const handleCapacityMessage = useCallback(
    (payload: CapacityPayload) => {
      if (!capacity[payload.hallId]) return;
      setCapacity((c) => ({
        ...c,
        [payload.hallId]: {
          label: c[payload.hallId].label || payload.hallName,
          capacity: payload.capacity,
        },
      }));
    },
    [capacity]
  );

  useInfoDisplayMessageWebSocket(
    InfoDisplayMessageType.Capacity,
    handleCapacityMessage
  );
  useInfoDisplayMessageWebSocket(
    InfoDisplayMessageType.Settings,
    handleSettingsMessage
  );
  useEffect(() => {
    if (hasUpdate) window.location.reload();
  }, [hasUpdate]);

  if (!settings) return null;
  return <ConfiguredView settings={settings} capacity={capacity} />;
};

const ConfiguredView = ({
  settings,
  capacity,
}: {
  settings: SettingsPayload;
  capacity: HallsCapacity;
}) => {
  const { search } = useLocation();
  const viewParams = useMemo(() => {
    const allViewParams = ["capacity", "lanesDirections"];
    const searchKeys = Array.from(new URLSearchParams(search).keys());
    const visibleViews = searchKeys.filter((k) => allViewParams.includes(k));
    return visibleViews.length ? visibleViews : ["lanesDirections"];
  }, [search]);

  const debug = useIsDebugEnabled();

  return (
    <Container $debug={debug}>
      <OrientationContainer>
        <ViewsContainer>
          {viewParams.map((p) => {
            switch (p) {
              case "capacity":
                return <InfoDisplayCapacityView key={p} capacity={capacity} />;
              case "lanesDirections":
                return (
                  <InfoDisplayEntranceExitView key={p} settings={settings} />
                );
              default:
                return null;
            }
          })}
        </ViewsContainer>
        <Version>
          <span>{packageJson.version}</span>
        </Version>
        <Logo>
          <PoweredBy />
        </Logo>
      </OrientationContainer>
    </Container>
  );
};

const Container = styled.div<{ $debug?: boolean }>`
  height: 100%;
  background: #000;
  cursor: ${({ $debug }) => ($debug ? "auto" : "none")};
`;

const ViewsContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: ${({ theme }) => (theme.landscape ? "row" : "column")};
`;

const Version = styled.div`
  position: absolute;
  display: flex;
  flex-align: center;
  align-items: center;
  right: 0;
  bottom: 0;
  color: #666;
`;

const logoScale = 10;
const logoAspectRatio = 1247 / 335.13;
const Logo = styled.div`
  position: absolute;
  left: 10px;
  bottom: 10px;
  color: #666;
  svg {
    display: block;
    ${({ theme }) =>
      theme.landscape
        ? `width: ${logoScale}${theme.vw}; height: auto;`
        : `width: auto; height: ${logoScale / logoAspectRatio}${theme.vh};`};
  }
`;
